import { useState } from 'react';
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DotsHorizontalIcon,
} from '@radix-ui/react-icons';
import type { EbookWorkspace } from '@wx-weiqi/contracts';
import { isEbookQuestionReady, questionStage } from '../services/ebook-editing';
import { ebookChapterNames, normalizeEbookChapter } from '../services/ebook-chapters';
import { EbookThumbnail } from './EbookReader';
import './ebook-library-arrange.css';

const PAGE_SIZE = 6;

export function EbookQuestionLibrary({
  data,
  onEdit,
  onLocate,
  onToggle,
  onAddMany,
  onArrange,
  onSave,
  dirty,
  busy,
}: {
  data: EbookWorkspace;
  onEdit: (id: string) => void;
  onLocate: (index: number) => void;
  onToggle: (id: string) => void;
  onAddMany: (ids: string[]) => void;
  onArrange: () => void;
  onSave: () => Promise<void>;
  dirty: boolean;
  busy: boolean;
}) {
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('');
  const [chapter, setChapter] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [selection, setSelection] = useState<string[]>([]);
  const [notice, setNotice] = useState('');
  const questions = data.questions;
  const chapters = ebookChapterNames(
    { ...data.book, questionIds: questions.map((q) => q.id) },
    questions,
  );
  const included = new Set(data.book.questionIds);
  const ready = new Set(questions.filter(isEbookQuestionReady).map((q) => q.id));
  const selected = selection.filter((id) => ready.has(id) && !included.has(id));
  const query = search.trim().toLocaleLowerCase();
  const matches = questions.filter(
    (q) =>
      (chapter === null || normalizeEbookChapter(q.chapter) === chapter) &&
      (!query ||
        [q.number, q.title, q.goal, q.chapter, q.category].some((t) =>
          t.toLocaleLowerCase().includes(query),
        )) &&
      (!source || String((q.source?.pageIndex ?? -1) + 1) === source) &&
      (!status ||
        (status === 'READY'
          ? ready.has(q.id)
          : status === 'INCLUDED'
            ? included.has(q.id)
            : questionStage(q) === status)),
  );
  const pageCount = Math.max(1, Math.ceil(matches.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const current = matches.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const selectable = current.filter((q) => ready.has(q.id) && !included.has(q.id)).map((q) => q.id);
  const allSelected = selectable.length > 0 && selectable.every((id) => selected.includes(id));
  const chooseChapter = (value: string | null) => {
    setChapter(value);
    setPage(0);
  };
  return (
    <section className="ebook-curation" aria-label="教材题库" aria-busy={busy}>
      <div className="ebook-curation-content">
        <aside className="ebook-curation-chapters" aria-label="按章节筛选题目">
          <h2>章节目录</h2>
          <nav>
            {[null, '', ...chapters].map((name) => (
              <button
                type="button"
                key={name === null ? 'all' : `chapter:${name}`}
                aria-pressed={chapter === name}
                onClick={() => chooseChapter(name)}
              >
                <span>{name === null ? '全部题目' : name || '未分章'}</span>
                <small>
                  {name === null
                    ? questions.length
                    : questions.filter((q) => normalizeEbookChapter(q.chapter) === name).length}
                </small>
              </button>
            ))}
          </nav>
        </aside>
        <div className="ebook-curation-main">
          <header className="ebook-curation-heading">
            <div>
              <h2>把准备好的题目加入电子书</h2>
              <p>
                共 {questions.length} 题 · {ready.size} 题已就绪
              </p>
            </div>
            <div className="ebook-curation-filters">
              <label className="ebook-curation-search">
                <span className="ebook-curation-sr-only">搜索题目</span>
                <input
                  type="search"
                  value={search}
                  placeholder="搜索题号、题目要求"
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                />
              </label>
              <label>
                <span className="ebook-curation-sr-only">完成状态</span>
                <select
                  aria-label="完成状态"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setPage(0);
                  }}
                >
                  <option value="">状态：全部</option>
                  <option value="READY">已就绪</option>
                  <option value="INCLUDED">已加入编排</option>
                  {['待校对', '待补信息', '待编答案', '自动批改就绪', '手动练习就绪'].map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label>
                <span className="ebook-curation-sr-only">来源 PDF 页</span>
                <select
                  aria-label="来源 PDF 页"
                  value={source}
                  onChange={(e) => {
                    setSource(e.target.value);
                    setPage(0);
                  }}
                >
                  <option value="">来源页：全部</option>
                  {[
                    ...new Set(
                      questions.flatMap((q) => (q.source ? [q.source.pageIndex + 1] : [])),
                    ),
                  ]
                    .sort((a, b) => a - b)
                    .map((n) => (
                      <option key={n} value={n}>
                        第 {n} 页
                      </option>
                    ))}
                </select>
              </label>
            </div>
          </header>
          <div className="ebook-curation-grid">
            {current.map((q) => {
              const stage = questionStage(q);
              const isReady = ready.has(q.id);
              const isIncluded = included.has(q.id);
              const isSelected = selected.includes(q.id);
              const label = q.number ? `第 ${q.number} 题` : q.title;
              return (
                <article
                  className={`ebook-curation-question${isSelected ? ' is-selected' : ''}`}
                  key={q.id}
                >
                  <div className="ebook-curation-question-heading">
                    <input
                      type="checkbox"
                      aria-label={`选择${label}`}
                      checked={isSelected}
                      disabled={busy || !isReady || isIncluded}
                      onChange={(e) => {
                        setNotice('');
                        setSelection(
                          e.target.checked
                            ? [...selected, q.id]
                            : selected.filter((id) => id !== q.id),
                        );
                      }}
                    />
                    <div>
                      <h3>
                        {label}
                        <span>{q.chapter}</span>
                      </h3>
                      <p>
                        {q.currentPlayer === 'BLACK' ? '黑先' : '白先'}
                        {q.source ? ` · PDF 第 ${q.source.pageIndex + 1} 页` : ''}
                      </p>
                    </div>
                    <details className="ebook-curation-menu">
                      <summary aria-label={`${label}更多操作`}>
                        <DotsHorizontalIcon />
                      </summary>
                      <div>
                        {q.source && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => onLocate(q.source!.pageIndex)}
                          >
                            定位原页
                          </button>
                        )}
                        {isIncluded && (
                          <button type="button" disabled={busy} onClick={() => onToggle(q.id)}>
                            移出编排
                          </button>
                        )}
                        {!q.source && !isIncluded && <span>暂无其他操作</span>}
                      </div>
                    </details>
                  </div>
                  <div className="ebook-curation-question-body">
                    <EbookThumbnail question={q} illustrated />
                    <div className="ebook-curation-question-summary">
                      <span className={`ebook-curation-stage${isReady ? ' is-ready' : ''}`}>
                        {isReady ? (q.mode === 'AUTO' ? '可自动批改' : '手动练习就绪') : stage}
                      </span>
                      <p title={q.goal}>{q.goal || '题目要求待填写'}</p>
                      {isIncluded && (
                        <span className="ebook-curation-included">
                          <CheckIcon aria-hidden="true" />
                          已加入编排
                        </span>
                      )}
                      <button type="button" disabled={busy} onClick={() => onEdit(q.id)}>
                        {isReady ? '编辑题目' : stage === '待编答案' ? '补录答案' : '继续校对'}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
            {!matches.length && (
              <div className="ebook-curation-empty">
                <h3>没有符合条件的题目</h3>
                <p>换一个章节或清除筛选条件。</p>
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setSource('');
                    setStatus('');
                    chooseChapter(null);
                  }}
                >
                  显示全部题目
                </button>
              </div>
            )}
          </div>
          <nav className="ebook-curation-pagination" aria-label="题库分页">
            <span>筛选结果 {matches.length} 题</span>
            <span>
              第 {currentPage + 1} / {pageCount} 页
            </span>
            <button
              type="button"
              aria-label="上一页题目"
              disabled={currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
            >
              <ChevronLeftIcon />
            </button>
            <button
              type="button"
              aria-label="下一页题目"
              disabled={currentPage + 1 === pageCount}
              onClick={() => setPage(currentPage + 1)}
            >
              <ChevronRightIcon />
            </button>
          </nav>
        </div>
      </div>
      <footer className="ebook-curation-footer">
        <div className="ebook-curation-selection">
          <label>
            <input
              type="checkbox"
              checked={allSelected}
              disabled={busy || !selectable.length}
              onChange={() =>
                setSelection(
                  allSelected
                    ? selected.filter((id) => !selectable.includes(id))
                    : [...new Set([...selected, ...selectable])],
                )
              }
            />
            选择本页就绪题
          </label>
          <span aria-live="polite">{notice || `已选 ${selected.length} 题`}</span>
          {selected.length > 0 && (
            <button
              type="button"
              disabled={busy}
              className="ebook-curation-link"
              onClick={() => setSelection([])}
            >
              清空
            </button>
          )}
        </div>
        <div className="ebook-curation-footer-actions">
          {dirty && (
            <button type="button" disabled={busy} onClick={() => void onSave()}>
              保存修改
            </button>
          )}
          <button type="button" disabled={busy} onClick={onArrange}>
            查看编排
          </button>
          <button
            type="button"
            className="primary"
            disabled={busy || !selected.length}
            onClick={() => {
              onAddMany(selected);
              setNotice(`已加入 ${selected.length} 题，请保存修改。`);
              setSelection([]);
            }}
          >
            加入编排{selected.length ? `（${selected.length}）` : ''}
          </button>
        </div>
      </footer>
    </section>
  );
}
